/**
 * @file Professional Profile Page — /profesional/:id
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  Clock,
  DollarSign,
  MapPin,
  Video,
} from "lucide-react";
import { useProfessionalDetail } from "../hooks/useProfessionals";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { useAuth } from "../hooks/useAuth";
import BookingFlow, { buildConfirmationData } from "../components/BookingFlow";
import PhoneVerificationModal from "../components/PhoneVerificationModal";
import ScheduleTable from "../components/ScheduleTable";
import { patientService } from "../services/patient";
import { SCHEDULE_STRINGS, COMMON_STRINGS, PUBLIC_STRINGS } from "../constants/strings";
import { Avatar, Button, Card, Chip } from "../ui";
import "./ProfessionalProfile.css";

export default function ProfessionalProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, handleAuthSuccess } = useAuth();
  const { professional, isLoading } = useProfessionalDetail(parseInt(id));
  const [myPatientId, setMyPatientId] = useState(null);

  useEffect(() => {
    if (user?.role !== "PACIENTE") return;
    patientService.getMe().then((res) => setMyPatientId(res.data.id)).catch(() => {});
  }, [user]);

  const booking = useBookingFlow({ professional, mode: "auth", patientId: myPatientId });
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);

  const handleConfirm = async ({ slot, reason }) => {
    if (!user?.isPhoneVerified) {
      setPendingBooking({ slot, reason });
      setShowPhoneModal(true);
      return;
    }
    await doBook(slot, reason);
  };

  const doBook = async (slot, reason) => {
    const confirmation = buildConfirmationData({
      professional,
      selectedLocation: booking.selectedLocation,
      selectedService: booking.selectedService,
      selectedDate: booking.selectedDate,
      slot,
      reason,
    });
    try {
      await booking.bookAppointment({
        dateTime: slot.dateTime,
        endTime: new Date(
          new Date(slot.dateTime).getTime() + professional.sessionDuration * 60 * 1000
        ).toISOString(),
        reason,
      });
      setConfirmedBooking(confirmation);
    } catch {
      // ignore
    }
  };

  const handlePhoneVerified = async (authData) => {
    if (authData) handleAuthSuccess(authData);
    setShowPhoneModal(false);
    if (pendingBooking) {
      await doBook(pendingBooking.slot, pendingBooking.reason);
      setPendingBooking(null);
    }
  };

  if (isLoading) {
    return (
      <div className="ppr-page">
        <div className="sk-block sk-h-360" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="ppr-page">
        <p>{COMMON_STRINGS.NOT_FOUND_PROFESSIONAL}</p>
      </div>
    );
  }

  const fullName = [professional.title, professional.user.firstName, professional.user.lastName].filter(Boolean).join(" ");
  const initials = `${professional.user.firstName?.[0] || ""}${professional.user.lastName?.[0] || ""}`.toUpperCase();
  const isPatient = user?.role === "PACIENTE";

  return (
    <div className="ppr-page">
      <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
        {COMMON_STRINGS.BACK}
      </Button>

      <Card>
        <div className="ppr-head">
          <Avatar size="xl" src={professional.photoUrl} initials={initials} alt={fullName} />
          <div className="ppr-head-text">
            <h1 className="ppr-name">{fullName}</h1>
            {professional.specialties?.length > 0 && (
              <div className="ppr-specialties">
                {professional.specialties.map((sp) => (
                  <Chip key={sp.id} tone="primary" size="sm">
                    {sp.cedula ? `${sp.name} · Céd. ${sp.cedula}` : sp.name}
                  </Chip>
                ))}
              </div>
            )}
            <div className="ppr-meta-row">
              {professional.price && (
                <span className="ppr-meta">
                  <DollarSign size={14} />${Number(professional.price).toLocaleString("es-MX")} {professional.currency}
                </span>
              )}
              <span className="ppr-meta">
                <Clock size={14} />{professional.sessionDuration} min
              </span>
              <span className="ppr-meta">
                <Mail size={14} />{professional.user.email}
              </span>
              {professional.user.phone && (
                <span className="ppr-meta">
                  <Phone size={14} />{professional.user.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {professional.bio && (
          <div className="ppr-section">
            <p className="ppr-bio">{professional.bio}</p>
          </div>
        )}

        {professional.schedules?.length > 0 && (
          <div className="ppr-section">
            <h3 className="ppr-section-title">{SCHEDULE_STRINGS.TITLE}</h3>
            <ScheduleTable schedules={professional.schedules} />
          </div>
        )}

        {professional.locations?.length > 0 && (
          <div className="ppr-section">
            <h3 className="ppr-section-title">Ubicaciones</h3>
            <ul className="ppr-locations">
              {professional.locations.map((loc) => (
                <li key={loc.id} className="ppr-location">
                  {loc.isVirtual ? <Video size={14} /> : <MapPin size={14} />}
                  <span>
                    {loc.isVirtual
                      ? loc.name || "Teleconsulta"
                      : [loc.name, loc.address, loc.colonia, loc.city, loc.state]
                          .filter(Boolean)
                          .join(", ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {isPatient && professional.slug && (
        <>
          <BookingFlow
            professional={professional}
            booking={booking}
            onConfirm={handleConfirm}
            isBooking={booking.isBooking}
            confirmedBooking={confirmedBooking}
            confirmButtonLabel={
              user?.isPhoneVerified ? PUBLIC_STRINGS.CONFIRM_BOOKING : "Continuar con mi número"
            }
          />
          <PhoneVerificationModal
            open={showPhoneModal}
            onClose={() => setShowPhoneModal(false)}
            onVerified={handlePhoneVerified}
          />
        </>
      )}
    </div>
  );
}
